import { useMutation, useQueryClient } from "@tanstack/react-query";

import { updateStage } from "@/adapters/api";
import type { Stage } from "../types.ts";
import { invalidateStageTree, stageKeys } from "../utils/query-keys.ts";
import type { SiblingMove } from "../utils/tree.ts";
import { useStagesLockedGuard } from "./use-stages-locked-guard.ts";

interface StagesCache {
	stages: Stage[];
}

export function useReorderStage(constructionId: string | undefined) {
	const queryClient = useQueryClient();
	const queryKey = stageKeys.list(constructionId);
	const guardLocked = useStagesLockedGuard(constructionId);

	return useMutation({
		mutationFn: async (move: SiblingMove) => {
			if (!constructionId) throw new Error("missing construction");
			for (const { stageId, position } of move.changes) {
				await updateStage(constructionId, stageId, { position });
			}
		},
		onMutate: async (move) => {
			await queryClient.cancelQueries({ queryKey });
			const previous = queryClient.getQueryData<StagesCache>(queryKey);
			if (previous) {
				queryClient.setQueryData<StagesCache>(queryKey, {
					...previous,
					stages: move.stages,
				});
			}
			return { previous };
		},
		onError: (error, _move, context) => {
			if (context?.previous) {
				queryClient.setQueryData(queryKey, context.previous);
			}
			guardLocked(error);
		},
		onSettled: () => invalidateStageTree(queryClient, constructionId),
	});
}
